import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';

interface SessionTimeoutDialogProps {
  open: boolean;
  remainingTime: number;
  onExtend: () => void;
  onLogout: () => void;
}

const SessionTimeoutDialog: React.FC<SessionTimeoutDialogProps> = ({
  open,
  remainingTime,
  onExtend,
  onLogout
}) => {
  const { isDarkMode } = useTheme();
  const [timeLeft, setTimeLeft] = useState(remainingTime);

  const themeColors = {
    text: isDarkMode ? '#f1f5f9' : '#1e293b',
    textSecondary: isDarkMode ? '#cbd5e1' : '#64748b',
    accent: '#10d7e2',
    accentLight: '#2fdfeb',
    warning: '#eab308',
    error: '#ef4444'
  };

  useEffect(() => {
    setTimeLeft(remainingTime);
  }, [remainingTime]);

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px'
        }
      }}
    >
      <Box
        sx={{
          backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.08)',
          borderLeft: `4px solid ${themeColors.warning}`
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: themeColors.warning, fontWeight: 600, fontSize: '18px' }}>
          <WarningIcon sx={{ color: themeColors.warning, fontSize: '24px' }} />
          Session Timeout Warning
        </DialogTitle>
      </Box>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body1" sx={{ color: themeColors.text, lineHeight: 1.6 }}>
            Your session will expire due to inactivity. Please extend your session to continue.
          </Typography>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.12)' : 'rgba(15, 118, 110, 0.1)',
              borderRadius: '6px',
              border: `1px solid ${isDarkMode ? 'rgba(15, 118, 110, 0.3)' : 'rgba(15, 118, 110, 0.2)'}`,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: themeColors.accent,
                fontWeight: 600,
                fontSize: '24px',
                letterSpacing: '0.05em'
              }}
            >
              {minutes}:{seconds.toString().padStart(2, '0')}
            </Typography>
            <Typography variant="caption" sx={{ color: themeColors.textSecondary, display: 'block', mt: 1 }}>
              Time remaining
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: themeColors.textSecondary, lineHeight: 1.6 }}>
            Click "Extend Session" to continue working, or you will be logged out automatically.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1.5, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          onClick={onLogout}
          sx={{
            borderColor: themeColors.error,
            color: themeColors.error,
            transition: 'all 150ms ease-in-out',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.1)',
              borderColor: themeColors.error
            }
          }}
        >
          Logout
        </Button>
        <Button
          variant="contained"
          onClick={onExtend}
          color="primary"
          sx={{
            fontWeight: 500,
            transition: 'all 150ms ease-in-out'
          }}
        >
          Extend Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutDialog;
